import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { authApi } from '../api/auth';
import type { RootState } from '.';
import { readAuthSession, type AuthSession } from './authSession';

type RequestStatus = 'idle' | 'submitting' | 'success' | 'error';

interface AuthState {
  loginStatus: RequestStatus;
  recoveryStatus: RequestStatus;
  resetStatus: RequestStatus;
  error: string | null;
  recoveryEmail: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthorized: boolean;
}

const storedSession = readAuthSession();
const initialState: AuthState = {
  loginStatus: 'idle',
  recoveryStatus: 'idle',
  resetStatus: 'idle',
  error: null,
  recoveryEmail: null,
  accessToken: storedSession?.accessToken ?? null,
  refreshToken: storedSession?.refreshToken ?? null,
  isAuthorized: Boolean(storedSession)
};

function errorMessage(reason: unknown) {
  return reason instanceof Error ? reason.message : 'Ein unbekannter Fehler ist aufgetreten.';
}

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      return await authApi.login(credentials);
    } catch (reason) {
      return rejectWithValue(errorMessage(reason));
    }
  }
);

export const refreshSession = createAsyncThunk<AuthSession, void, { state: RootState; rejectValue: string }>(
  'auth/refreshSession',
  async (_, { getState, rejectWithValue }) => {
    const refreshToken = getState().auth.refreshToken;
    if (!refreshToken) return rejectWithValue('Keine aktualisierbare Sitzung vorhanden.');
    try {
      return await authApi.refresh(refreshToken);
    } catch (reason) {
      return rejectWithValue(errorMessage(reason));
    }
  }
);

export const requestPasswordRecovery = createAsyncThunk(
  'auth/requestPasswordRecovery',
  async (email: string, { rejectWithValue }) => {
    try {
      await authApi.requestPasswordRecovery(email);
      return email;
    } catch (reason) {
      return rejectWithValue(errorMessage(reason));
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (payload: { token: string; password: string }, { rejectWithValue }) => {
    try {
      await authApi.resetPassword(payload);
    } catch (reason) {
      return rejectWithValue(errorMessage(reason));
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthFeedback(state) {
      state.error = null;
      state.loginStatus = 'idle';
      state.recoveryStatus = 'idle';
      state.resetStatus = 'idle';
    },
    logout(state) {
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthorized = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => { state.loginStatus = 'submitting'; state.error = null; })
      .addCase(login.fulfilled, (state, action) => {
        state.loginStatus = 'success';
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthorized = true;
      })
      .addCase(login.rejected, (state, action) => { state.loginStatus = 'error'; state.error = String(action.payload); })
      .addCase(refreshSession.fulfilled, (state, action) => {
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthorized = true;
      })
      .addCase(refreshSession.rejected, (state) => {
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthorized = false;
      })
      .addCase(requestPasswordRecovery.pending, (state) => { state.recoveryStatus = 'submitting'; state.error = null; })
      .addCase(requestPasswordRecovery.fulfilled, (state, action) => {
        state.recoveryStatus = 'success';
        state.recoveryEmail = action.payload;
      })
      .addCase(requestPasswordRecovery.rejected, (state, action) => {
        state.recoveryStatus = 'error';
        state.error = String(action.payload);
      })
      .addCase(resetPassword.pending, (state) => { state.resetStatus = 'submitting'; state.error = null; })
      .addCase(resetPassword.fulfilled, (state) => { state.resetStatus = 'success'; })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetStatus = 'error';
        state.error = String(action.payload);
      });
  }
});

export const { clearAuthFeedback, logout } = authSlice.actions;
export const authReducer = authSlice.reducer;
